import { useMemo } from 'react'
import { Box } from '@mui/material'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { SheduleTableProps, SchedulerTanksRowType } from './Scheduler.types'

/**
 * Read a boolean that may have come back as a string.
 *
 * The service writes every column with pandas' `dtype=str`, so a `false`
 * posted from here is stored and returned as the STRING `'False'` — and a
 * non-empty string is truthy in JavaScript, so the checkbox springs back to
 * ticked when it is read again. Mirrors the service's own `_is_truthy` and the
 * copy in `SchedulerTrials.muiTable.tsx`, where the same bug was found first.
 */
const asBool = (v: unknown): boolean => {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    return s === 'true' || s === '1' || s === 'yes'
  }
  return false
}

/**
 * The vessels themselves — `TanksDelta`.
 *
 * Only `tankId` is required.
 *
 * `maxHoldHours` IS enforced, on a consolidated multi-week run: it sizes the
 * vessel's fill into batches, because one batch can only serve drains spread
 * over `maxHoldHours - maturationHours`. Left empty it bounds nothing and the
 * horizon is made as a single batch, which is what every schedule did before.
 *
 * `allowAccumulation` lets several kitchen runs stack in one vessel, and is
 * what makes `capacity` bind — off, the batch is bounded by the run that fills
 * it and the vessel imposes nothing, exactly as before. `UOM` is display only.
 */
const dataConfig = {
  required: ['tankId'],
  optional: ['capacity', 'UOM', 'maxHoldHours', 'allowAccumulation'],
}

export const SchedulerTanksTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  deleteTableData,
  isDeleting,
}: SheduleTableProps<SchedulerTanksRowType>) => {
  const handleDataUpdate = (
    changedRows: Record<number, SchedulerTanksRowType>
  ) => {
    updateTableData(changedRows)
  }

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Tank',
        field: 'tankId',
        type: 'string',
        flex: 1,
        editable: true,
      },
      {
        headerName: 'Capacity',
        field: 'capacity',
        type: 'number',
        flex: 1,
        editable: true,
        description:
          'How much this vessel holds. Read ONLY when Accumulate is ticked, which it is by default, and then it is the ceiling a top-up must fit under. With Accumulate off the filling is bounded by the kitchen run that makes it and the vessel imposes nothing. Leave empty for no limit — blank means unbounded, not "no top-ups".',
      },
      {
        headerName: 'Accumulate',
        field: 'allowAccumulation',
        type: 'boolean',
        // Coerced: the stored value is the string 'True'/'False'.
        valueGetter: (value: unknown) => asBool(value),
        flex: 0.8,
        editable: true,
        description:
          'ON BY DEFAULT. A kitchen run may add to what this vessel already holds, provided it is the SAME product, up to Capacity. Untick it and the vessel takes a new batch only once its contents have been drawn out completely and it has been cleaned - so a filling is one kitchen run, and a quantity larger than (kitchenRunRate x maxRunTime) cannot be made at all and reports as unmet demand. Maturity always runs from the MOST RECENT run into the vessel, so a later top-up delays the whole contents rather than being free; the maximum hold still runs from the first. Splitting ONE filling across several runs happens on a CONSOLIDATED multi-week run.',
      },
      {
        headerName: 'UOM',
        field: 'UOM',
        type: 'string',
        flex: 1,
        editable: true,
      },
      {
        headerName: 'Max Hold (h)',
        field: 'maxHoldHours',
        type: 'number',
        flex: 1,
        editable: true,
        description:
          'The longest this vessel may hold a batch, from the end of the fill that made it to the end of the last drain that empties it. Enforced on a CONSOLIDATED multi-week run: one batch can only serve drains spread over (Max Hold - maturationHours), so a horizon whose demand is spread wider than that is made as several batches, each filled only after the previous one has been drained and the vessel cleaned. Leave empty for no limit, which makes one batch however long it is held. Ignored on a single-week run, where the fill and its drain are in different runs.',
      },
    ],
    []
  )

  return (
    <Box>
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="SchedulerTanks"
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
        multiStageUpload={{
          uploadFile: uploadScheduleData,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          originalData: data,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
      />
    </Box>
  )
}
